import * as React from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Drawer, notification } from "antd";
import { ErrorBoundary } from "./errorBoundary";
import { getAnalytics, logEvent } from "firebase/analytics";
import { ALL_DOMAINS_UPDATE_INTERVAL_MS } from "./envVariables";
import { request } from "./services/cpln";
import { Loader } from "./components/layout/loader";
import { OptimalViewingModal } from "./optimalViewingModal";
import * as Sentry from "@sentry/react";
import { GenericAlertContent } from "./genericAlertContent";
import { DrawerContext, useInitDrawerContext } from "./components/antd/drawerContext";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import { selectIsAdmin } from "./store/userSlice";
import { getFirebaseApp } from "./store/firebase";
import { selectContext, setLinkedAccountId, setRedirect } from "./store/contextSlice";
import { fetchDomainItems } from "./store/restSlice";
import { updateLastActivityTimestamp } from "./store/uiDataSlice";
import { Guides } from "./guides/guides";
import { WorkloadViewModal } from "./pages/workload/viewModal";
import { NotificationDrawer } from "./components/NotificationDrawer";
import { DocumentTitle } from "./components/documentTitle/DocumentTitle";

export const GlobalElement: React.FC = () => {
  const dispatch = useAppDispatch();
  const isAdmin = useAppSelector(selectIsAdmin);
  const firebaseInitialized = useAppSelector((state) => state.user.firebaseInitialized);
  const userIsProcessing = useAppSelector((state) => state.user.isProcessing);
  const redirect = useAppSelector((state) => state.context.redirect);
  const context = useAppSelector(selectContext);
  const isChangingContext = useAppSelector((state) => state.context.isChangingContext);
  const adminOrg = useAppSelector((state) => state.user.org);

  // tasks
  const newInviteCounter = useAppSelector((state) => state.rest.newInviteCounter);
  const taskIntervalMs = useAppSelector((state) => state.rest.intervalMs);
  const taskRequestCounter = useAppSelector((state) => state.rest.taskRequestCounter);

  const domainRequestCounter = useAppSelector((state) => state.rest.domainRequestCounter);

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const prevPathnameRef = React.useRef(pathname);
  React.useEffect(() => {
    console.log(`[NAV] ${prevPathnameRef.current} -> ${pathname}`);
    prevPathnameRef.current = pathname;
  }, [pathname]);
  const buttonHeightRef = React.useRef({
    current: 0,
    initial: 0,
    target: 0,
  });
  const [buttonTriggerer, setButtonTriggerer] = React.useState(0);

  React.useEffect(() => {
    const action = () => {
      dispatch(updateLastActivityTimestamp());
    };
    window.addEventListener("keydown", action);
    window.addEventListener("mousedown", action);
    window.addEventListener("focus", action);
    return () => {
      window.removeEventListener("keydown", action);
      window.removeEventListener("mousedown", action);
      window.removeEventListener("focus", action);
    };
  }, []);

  // Modifying the position (height) of Sentry's button
  React.useEffect(() => {
    const callback = (entries) => {
      const actionThreshold = 0.6;
      const entry = entries[0];
      const intersectionRatio = entry.intersectionRatio;
      const target = entry.target;
      const isVisible = intersectionRatio > actionThreshold;
      const root = document.getElementById("sentry-feedback");

      if (!root) {
        return;
      }

      // Ensure that the target has loaded and has a defined height
      if (target && target.getClientRects().length > 0) {
        const targetHeight = target.getClientRects()[0].height;
        buttonHeightRef.current = {
          target: isVisible ? targetHeight : 0,
          initial: buttonHeightRef.current.current,
          current: buttonHeightRef.current.current,
        };
        setButtonTriggerer((x) => x + 1);
      } else {
        console.warn("Target height could not be determined");
      }
    };

    const observerOptions = {
      root: document.body,
      threshold: [0.0, 0.6, 1.0],
    };

    const observer = new IntersectionObserver(callback, observerOptions);
    const target = document.querySelector("footer.footer");

    if (!target) {
      return;
    }

    observer.observe(target);

    return () => {
      observer.unobserve(target);
    };
  }, [pathname]);

  React.useEffect(() => {
    const valsCache = buttonHeightRef.current;

    if (Math.abs(valsCache.current - valsCache.target) < 1) {
      return;
    }

    const duration = 100;
    const interval = 5;
    const id = setInterval(() => {
      const vals = buttonHeightRef.current;
      const root = document.getElementById("sentry-feedback");

      if (!root) {
        return;
      }

      const value = `auto 20px ${vals.current}px auto`;
      (root as HTMLElement).style.setProperty("--actor-inset", value);

      const increment = Math.abs(vals.target - vals.initial) / (duration / interval);

      let newCurrent = vals.current;
      if (vals.target > vals.current) {
        newCurrent = vals.current + increment;
        if (newCurrent > vals.target) {
          newCurrent = vals.target;
          setButtonTriggerer((x) => x + 1);
        }
      } else if (vals.target < vals.current) {
        newCurrent = vals.current - increment;
        if (newCurrent < vals.target) {
          newCurrent = vals.target;
          setButtonTriggerer((x) => x + 1);
        }
      }

      buttonHeightRef.current = {
        ...vals,
        current: newCurrent,
      };
    }, interval);
    return () => {
      if (id) {
        clearInterval(id);
      }
    };
  }, [buttonTriggerer]);

  React.useEffect(() => {
    if (!firebaseInitialized) {
      return;
    }
    if (isAdmin) {
      return;
    }
    logEvent(getAnalytics(getFirebaseApp()), "page_view");
  }, [pathname, firebaseInitialized]);

  React.useEffect(() => {
    if (redirect) {
      console.log(`[NAV] setRedirect: ${window.location.pathname} -> ${redirect}`);
      if (window.location.pathname === redirect) {
        dispatch(setRedirect(null));
        return;
      }
      setTimeout(() => {
        navigate("/redirect");
      }, 0);
    }
  }, [redirect]);

  React.useEffect(() => {
    if (!context.org) {
      dispatch(setLinkedAccountId(""));
      return;
    }
    fetchLinkedAccount();
  }, [context.org]);

  // TODO not a great method, improve this, interferes with other functionality
  React.useEffect(() => {
    if (userIsProcessing) {
      return;
    }

    if (
      !context.org &&
      !adminOrg &&
      !window.location.pathname.startsWith(`/billing`) &&
      !window.location.pathname.startsWith(`/home/account`) &&
      window.location.pathname !== `/home` &&
      window.location.pathname !== `/console`
    ) {
      console.log(`[NAV] no-org effect: ${window.location.pathname} -> /home/org`, { userIsProcessing, context });
      dispatch(setRedirect(`/home/org`));
      return;
    }

    try {
      const oldOrg = window.location.pathname.split(`/org/`)[1].split(`/`)[0];
      const newPath = window.location.pathname.replace(`/org/${oldOrg}`, `/org/${context.org}`);
      dispatch(setRedirect(newPath));
    } catch (e) {}
  }, [userIsProcessing, adminOrg, context.org]);

  async function fetchLinkedAccount() {
    try {
      const { data } = await request({ service: "billing-ng", url: `/org/${context.org}/account` });
      dispatch(setLinkedAccountId(data.id));
      if (context.org) {
        Sentry.setTag("org", context.org);
      }
      if (data.id) {
        Sentry.setTag("account", data.id);
      }
    } catch (e) {
      dispatch(setLinkedAccountId(""));
    }
  }

  React.useEffect(() => {
    if (!newInviteCounter) {
      return;
    }

    notification.info({
      message: "New Task",
      description: (
        <div>
          <span style={{ color: "var(--color-link)", cursor: "pointer" }} onClick={() => navigate("/task")}>
            Click
          </span>{" "}
          to see tasks
        </div>
      ),
    });
  }, [newInviteCounter]);

  React.useEffect(() => {
    const id = setTimeout(() => {
      // TODO fix
      // checkTasks();
    }, taskIntervalMs);
    return () => {
      if (id) {
        clearTimeout(id);
      }
    };
  }, [taskRequestCounter]);

  // Fetch all domain items for current domain endpoints, at the initialization of the app
  React.useEffect(() => {
    if (!context.org) {
      return;
    }
    dispatch(fetchDomainItems());
  }, [context.org]);

  // Fetch all domain items for current domain endpoints, reschedule it after each request is completed
  React.useEffect(() => {
    if (!context.org) {
      return;
    }
    const id = setTimeout(() => {
      dispatch(fetchDomainItems());
    }, ALL_DOMAINS_UPDATE_INTERVAL_MS);
    return () => {
      if (id) {
        clearTimeout(id);
      }
    };
  }, [context.org, domainRequestCounter]);

  const drawerHook = useInitDrawerContext();

  return (
    <DrawerContext.Provider value={drawerHook}>
      <DocumentTitle />
      <ErrorBoundary>
        <Outlet />
        <Drawer
          className="persistent-drawer"
          open={drawerHook.persistent.isOpen}
          title={drawerHook.persistent.title}
          onClose={drawerHook.closePersistent}
          closable
          maskClosable={true}
          mask={true}
          width={drawerHook.persistent.width}
          destroyOnHidden
        >
          {drawerHook.persistent.content}
          <Drawer
            className="temporary-drawer"
            open={drawerHook.persistent.isOpen && drawerHook.temporary.isOpen}
            title={drawerHook.temporary.title}
            onClose={drawerHook.closeTemporary}
            closable
            maskClosable={false}
            mask={false}
            width={drawerHook.temporary.width}
            destroyOnHidden
          >
            {drawerHook.temporary.content}
          </Drawer>
        </Drawer>
        <Drawer
          className="temporary-drawer"
          open={!drawerHook.persistent.isOpen && drawerHook.temporary.isOpen}
          title={drawerHook.temporary.title}
          onClose={drawerHook.closeTemporary}
          closable
          maskClosable={false}
          mask={false}
          width={drawerHook.temporary.width}
          destroyOnHidden
        >
          {drawerHook.temporary.content}
        </Drawer>
        <GenericAlertContent />
        <OptimalViewingModal />
        <WorkloadViewModal />
        <Guides />
        <NotificationDrawer />
        {isChangingContext ? (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 10_000,
              width: "100vw",
              height: "100vh",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              backdropFilter: "blur(5px)",
            }}
          >
            <Loader />
            <div
              style={{ top: "15%", color: "white" }}
              className="absolute inset-0 flex flex-col gap-2 items-center justify-center"
            >
              <span>Processing</span>
              <span>Please wait...</span>
            </div>
          </div>
        ) : null}
      </ErrorBoundary>
    </DrawerContext.Provider>
  );
};
